import React from "react";
import { ErrorStateView } from "./ErrorStateView";
import { EmptyStateView } from "./EmptyStateView";

export interface DataStateViewProps {
	/** When true, renders loadingContent instead of children */
	loading?: boolean;
	/** When set, renders error state. Takes precedence over empty and content */
	error?: string | null;
	/** When true and not loading/error, renders empty state */
	isEmpty?: boolean;
	/** Callback for retry button in error state */
	onRetry?: () => void;
	/** Content to show when loading (e.g. skeleton) */
	loadingContent: React.ReactNode;
	/** Title for error state. Default: "Failed to load" */
	errorTitle?: string;
	/** Title for empty state (ignored when `emptyContent` is set) */
	emptyTitle?: string;
	/** Description for empty state (ignored when `emptyContent` is set) */
	emptyDescription?: string;
	/** Optional custom icon for empty state */
	emptyIcon?: React.ReactNode;
	/** When set, replaces the default empty title/description view */
	emptyContent?: React.ReactNode;
	/** Main content when not loading, no error, and not empty */
	children: React.ReactNode;
}

/**
 * Renders one of: loading → error → empty → children.
 * Use in Connectors, Topology, or any tab that has loading/error/empty states.
 */
export const DataStateView: React.FC<DataStateViewProps> = ({
	loading = false,
	error = null,
	isEmpty = false,
	onRetry,
	loadingContent,
	errorTitle = "Failed to load",
	emptyTitle = "No data",
	emptyDescription = "",
	emptyIcon,
	emptyContent,
	children,
}) => {
	if (loading) {
		return <>{loadingContent}</>;
	}
	if (error) {
		return (
			<ErrorStateView
				message={error}
				title={errorTitle}
				onRetry={onRetry}
			/>
		);
	}
	if (isEmpty) {
		if (emptyContent != null) {
			return <>{emptyContent}</>;
		}
		return (
			<EmptyStateView
				title={emptyTitle}
				description={emptyDescription}
				icon={emptyIcon}
			/>
		);
	}
	return <>{children}</>;
};

export default DataStateView;
